import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterDef } from '@shared-ui/components';
import type { FilterOptions } from '../../../api/questionQualityApi';
import {
    OWNER_LABEL, OWNERS, RESOLUTION_LABEL, RESOLUTION_STATUSES, IMPORT_STATUS_LABEL, IMPORT_STATUSES,
} from '../../../api/questionQualityApi';
import { FILTER_PREFIX, PAGE_PARAM, SUBJECTS } from './verdictDisplay';

/**
 * The filters, and the one predicate both views narrow by.
 *
 * They sit above the tab strip because they mean the same thing on both sides of
 * it: By Question renders the surviving questions as rows, By Rule counts the
 * same survivors per rule. When each view had its own bar over its own endpoint,
 * that was not expressible — the two answered with different populations, so
 * "critical findings in Coordination Compounds" could be asked of one and not the
 * other.
 *
 * State lives in the URL: opening a question's findings unmounts this, and a
 * filtered list is worth being able to send to somebody.
 */

/** Which of a question's own attributes the filters read. Everything here is a
 *  question-level fact, which is why one predicate can serve both views. */
export interface QualityFilters {
    topic?: string;
    rule?: string;
    /** Severity levels and `unsure`, comma-joined — one axis, any of. */
    issue?: string;
    grade?: string;
    board?: string;
    /** Public / Tenant / Teacher — see `QuestionOwner`. */
    owner?: string;
    resolution?: string;
    /** Live / Draft — the question's own publish state. */
    importStatus?: string;
    detectedFrom?: string;
    question?: string;
}

export function useQualityFilters() {
    const [searchParams, setSearchParams] = useSearchParams();

    const subject = searchParams.get('qs') || 'chemistry';
    const page = Number(searchParams.get(PAGE_PARAM)) || 1;

    const filters = useMemo(() => {
        const out: Record<string, string> = {};
        searchParams.forEach((v, k) => {
            if (k.startsWith(FILTER_PREFIX) && k !== PAGE_PARAM && v) out[k.slice(FILTER_PREFIX.length)] = v;
        });
        return out;
    }, [searchParams]);

    const write = useCallback((next: Record<string, string>, nextPage: number, nextSubject?: string) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            if (nextSubject) params.set('qs', nextSubject);
            // Drop every filter key first: an unset filter must leave the URL, not
            // linger as an empty value that reads as "still filtered".
            for (const key of [...params.keys()]) {
                if (key.startsWith(FILTER_PREFIX)) params.delete(key);
            }
            for (const [k, v] of Object.entries(next)) if (v) params.set(FILTER_PREFIX + k, v);
            if (nextPage > 1) params.set(PAGE_PARAM, String(nextPage)); else params.delete(PAGE_PARAM);
            return params;
        }, { replace: true });
    }, [setSearchParams]);

    /**
     * A subject change clears the filters, in the SAME update.
     *
     * Two writes in one tick do not survive: React Router hands a functional
     * updater the params as of THIS render, so the second is computed from a URL
     * without the first in it. And they belong together anyway — the topic, board
     * and grade options are built from the rows themselves, so old choices would
     * filter the new subject down to nothing and read as "no findings".
     */
    const setSubject = useCallback((next: string) => write({}, 1, next), [write]);
    const setFilters = useCallback((next: Record<string, string>) => write(next, 1), [write]);
    const setPage = useCallback((next: number) => write(filters, next), [write, filters]);

    return { subject, setSubject, filters, setFilters, page, setPage };
}

/**
 * The bar itself.
 *
 * Options describe the SUBJECT and come from their own endpoint. They used to be
 * derived from the rows that had come back, which made them a function of the
 * page: a topic present only in questions the server had cut could not be
 * chosen, so the filter that would have found them was the one you could not
 * set.
 */
export function buildFilterDefs(input: {
    rules: string[];
    options: FilterOptions | null;
    subjectLabel: (s: string) => string;
}): FilterDef[] {
    const { rules, options, subjectLabel } = input;
    return [
        {
            // The scope. `required`: the ledger is read one partition at a time,
            // so there is no "any subject" it could answer.
            key: 'subject',
            placeholder: 'Subject',
            required: true,
            options: SUBJECTS.map(s => ({ value: s, label: subjectLabel(s) })),
        },
        // Topic itself is not a built-in filter def — it renders as a
        // hierarchical TopicSelector via `renderAfter`, matching every other
        // module-scoped picker in the app (see PastActivity.tsx). It still
        // commits straight to the URL like the rest of the bar; only its
        // rendering skips the flat `<select>` shape.
        // Board then grade, straight after the topic: the three of them name the
        // same thing — which syllabus this question is FOR — and a reader picks
        // them in that order, widest first. The check and the severity are a
        // different question (how it is failing), so they follow.
        {
            key: 'board',
            placeholder: 'Any board',
            options: (options?.boards ?? []).map(b => ({ value: b, label: b.toUpperCase() })),
        },
        {
            key: 'grade',
            placeholder: 'Any grade',
            options: (options?.grades ?? []).map(g => ({ value: g, label: `Grade ${g}` })),
        },
        { key: 'rule', placeholder: 'Any check', options: rules.map(r => ({ value: r, label: r })) },
        {
            /**
             * Severity and "fail or unsure" were two selects that could
             * contradict each other — unsure AND critical is a combination no
             * finding has, and picking it emptied the table with no hint why.
             * One axis, multi-select, because "critical or major" is the ordinary
             * triage question and two single selects could not ask it.
             */
            key: 'issue',
            kind: 'multiselect',
            placeholder: 'Any severity',
            options: [
                { value: 'critical', label: 'Critical' },
                { value: 'major', label: 'Major' },
                { value: 'minor', label: 'Minor' },
                { value: 'unsure', label: 'Unsure' },
            ],
        },
        {
            // Always offered, even where the ledger is all public today. Teacher
            // questions are coming into the judged corpus, and a filter that
            // appears and disappears with the subject is one a reader stops
            // looking for.
            key: 'owner',
            placeholder: 'Any owner',
            options: OWNERS.map(o => ({ value: o, label: OWNER_LABEL[o] })),
        },
        {
            key: 'resolution',
            placeholder: 'Any resolution',
            options: RESOLUTION_STATUSES.map(r => ({ value: r, label: RESOLUTION_LABEL[r] })),
        },
        {
            // The question's own publish state — orthogonal to what a check found.
            // A draft is unreviewed on purpose, so "what's wrong with what's
            // actually live" and "what's wrong with what's still in the pipeline"
            // are different questions this answers.
            key: 'importStatus',
            placeholder: 'Any status',
            options: IMPORT_STATUSES.map(s => ({ value: s, label: IMPORT_STATUS_LABEL[s] })),
        },
        {
            // A rolling window, not two open date pickers — "how recent" is the
            // question a reader actually has, and a from/to pair made them do the
            // subtraction themselves. Options carry `daysAgo(n)` computed HERE
            // (not memoized away), because "today" moves.
            key: 'detectedFrom',
            placeholder: 'All time',
            options: [
                { value: daysAgo(0), label: 'Today' },
                { value: daysAgo(2), label: '< 3 days' },
                { value: daysAgo(6), label: '≤ 7 days' },
            ],
        },
        // `question` itself is not a built-in filter def — it renders as a
        // QuestionSearchFilter (search-and-pick, like AnalysisList's question
        // filter) rather than a plain text box.
    ];
}

/** `n` days before today, as a local `YYYY-MM-DD` — the same shape a native date
 *  input produces, and what the server's own `localDay()` compares against. */
function daysAgo(n: number): string {
    const d = new Date();
    d.setDate(d.getDate() - n);
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
